import type { FontCategory, FontRole, FontScheme } from "@/lib/types";

/** Roles the uploader styles independently (mirrors the backend). */
export const FONT_ROLES = ["heading", "body"] as const;

/** Google Fonts categories used in the picker. */
export const FONT_CATEGORIES: FontCategory[] = [
  "serif",
  "sans-serif",
  "display",
  "handwriting",
  "monospace",
];

export const GOOGLE_FONTS_CSS_PREFIX = "https://fonts.googleapis.com/css2?";

/** Default pairing preserving the original uploader duo. */
export const DEFAULT_FONT_SCHEME: FontScheme = {
  heading: { family: "Old Standard TT", category: "serif" },
  body: { family: "Inter", category: "sans-serif" },
  cssUrl:
    GOOGLE_FONTS_CSS_PREFIX +
    "family=Old+Standard+TT:ital,wght@0,400;0,700;1,400&" +
    "family=Inter:wght@300;400;500;600&display=swap",
};

const DESIRED_WEIGHTS: Record<FontRole, number[]> = {
  heading: [400, 700],
  body: [300, 400, 500, 600],
};

const DESIRED_ITALIC: Record<FontRole, boolean> = {
  heading: true,
  body: false,
};

interface ParsedVariants {
  weights: Set<number>;
  italics: Set<number>;
}

/**
 * Parses Web Fonts API variant strings into available normal/italic weights.
 * Mirrors the backend `parseVariants` so admin previews match what is stored.
 */
function parseVariants(variants: string[]): ParsedVariants {
  const weights = new Set<number>();
  const italics = new Set<number>();
  for (const variant of variants) {
    const isItalic = variant === "italic" || variant.endsWith("italic");
    if (variant === "regular" || variant === "italic") {
      (isItalic ? italics : weights).add(400);
      continue;
    }
    const num = parseInt(variant, 10);
    if (Number.isFinite(num)) {
      (isItalic ? italics : weights).add(num);
    }
  }
  return { weights, italics };
}

function buildFamilyParam(
  family: string,
  role: FontRole,
  variants: string[],
): string {
  const parsed = parseVariants(variants);
  const desired = DESIRED_WEIGHTS[role];
  let weights = desired.filter((w) => parsed.weights.has(w));
  if (!weights.includes(400)) weights = [400, ...weights];
  weights = Array.from(new Set(weights)).sort((a, b) => a - b);

  const wantItalic = DESIRED_ITALIC[role] && parsed.italics.has(400);
  const encodedName = family.trim().replace(/ +/g, "+");

  if (wantItalic) {
    const tuples = [...weights.map((w) => `0,${w}`), "1,400"];
    return `family=${encodedName}:ital,wght@${tuples.join(";")}`;
  }
  return `family=${encodedName}:wght@${weights.join(";")}`;
}

/**
 * Builds the Google Fonts CSS2 URL for a heading + body pairing. Mirrors the
 * backend so a manually-picked scheme stores the same `cssUrl` the AI path
 * would produce.
 */
export function buildGoogleFontsUrl(
  heading: { family: string; variants: string[] },
  body: { family: string; variants: string[] },
): string {
  const headingParam = buildFamilyParam(heading.family, "heading", heading.variants);
  const bodyParam = buildFamilyParam(body.family, "body", body.variants);
  return GOOGLE_FONTS_CSS_PREFIX + `${headingParam}&${bodyParam}&display=swap`;
}

/** CSS generic fallback for a category, used in previews and stored stacks. */
export function genericFallback(category: FontCategory): string {
  switch (category) {
    case "serif":
      return "serif";
    case "monospace":
      return "monospace";
    case "handwriting":
      return "cursive";
    case "display":
      return "system-ui";
    case "sans-serif":
    default:
      return "sans-serif";
  }
}
